import React, { useEffect, useRef } from 'react';

import {
  CountdownReloadToast,
  usePreventAutoReloadOnNewVersion as usePreventAutoReload,
} from '@eluve/blocks';
import { useToast } from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import { useCaptureEvent } from '@eluve/posthog-react';

import { useUserFileSystemStore } from './user-file-system.store';
import {
  clearUserFileSystemReloadAttempt,
  hasAttemptedUserFileSystemReload,
  markUserFileSystemReloadAttempted,
} from './userFileSystemReloadAttempt';

const TOAST_ID = 'file-system-unavailable-reload';

const EVENT_NAMES = {
  viewed: 'user_viewed_file_system_reload_toast',
  userDismissed: 'user_dismissed_file_system_reload_toast',
  forceDismissed: 'force_dismissed_file_system_reload_toast',
  userReloaded: 'user_reloaded_for_file_system',
  autoReloaded: 'auto_reloaded_for_file_system',
} as const;

export const UserFileSystemReloadGuard: React.FC = () => {
  const userId = useUserFileSystemStore((state) => state.userId);
  const userFileSystemType = useUserFileSystemStore(
    (state) => state.userFileSystem.type,
  );
  const toast = useToast();
  const logger = useNamedLogger('UserFileSystemReloadGuard');
  const captureEvent = useCaptureEvent();
  const toastOpenRef = useRef(false);

  useEffect(() => {
    if (userFileSystemType === 'available') {
      clearUserFileSystemReloadAttempt(userId);
      return;
    }

    if (userFileSystemType !== 'unavailable') {
      return;
    }

    if (toastOpenRef.current) {
      return;
    }

    if (hasAttemptedUserFileSystemReload(userId)) {
      logger.info(
        'Skipping file system auto-reload toast: already attempted in this browser context',
      );
      return;
    }

    if (usePreventAutoReload.getState().shouldPrevent()) {
      logger.info(
        'Skipping file system auto-reload toast: shouldPrevent is true',
      );
      return;
    }

    logger.info('Prompting user to reload to recover unavailable file system');

    toastOpenRef.current = true;

    const onDismiss = () => {
      toastOpenRef.current = false;
    };

    const onReload = () => {
      markUserFileSystemReloadAttempted(userId);
    };

    toast.message(
      <CountdownReloadToast
        toastId={TOAST_ID}
        title="Reloading to Recover"
        description="We hit a problem accessing local storage. Reloading the page usually fixes it."
        eventNames={EVENT_NAMES}
        captureEvent={captureEvent}
        onDismiss={onDismiss}
        onReload={onReload}
      />,
      {
        id: TOAST_ID,
        position: 'bottom-right',
        duration: Infinity,
        onDismiss,
      },
    );
  }, [captureEvent, logger, toast, userFileSystemType, userId]);

  return null;
};
